import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableSkeleton, TopProgress } from "../../../components";
import {
  Container,
  WrapperRow,
  Title,
  BodyText,
  GreenDot,
  Greentext,
  StateWrapper,
} from "../../cloud/Instances/styles";
import { useQueryParam, StringParam, NumberParam } from "use-query-params";
import { utilsService } from "../../../api";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { handleStatusName } from "../../../utils/handle-status-name";
import {
  EmptyValue,
  LogContainer,
  LogDetails,
  LogPrimary,
  UserInfo,
  UserMeta,
  UserName,
} from "./styles";

const DEFAULT_ORDERING = "-created_at";

type ActivityLogItem = {
  id: number;
  created_at: string;
  last_update?: string | null;
  state?: string | null;
  type?: string | null;
  log: string;
  details?: string | null;
  parameters?: string | null;
  tasks_count?: number;
  total_operation_count?: number;
  user?: {
    id?: number;
    email?: string | null;
    full_name?: string | null;
    display?: string | null;
  } | null;
};

const ActivityLog: FC = () => {
  const [t] = useTranslation();
  const [page] = useQueryParam("page", NumberParam);
  const [ordering, setOrdering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [paginationData, setPaginationData] = useState<any>();
  const [logs, setLogs] = useState<any[]>([]);

  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const formatStateLabel = useCallback(
    (state?: string | null) => {
      if (!state) {
        return t("NOT_AVAILABLE");
      }
      const normalized = state.toLowerCase();
      if (normalized === "success") {
        return t("SUCCEEDED");
      }
      if (normalized === "error") {
        return t("ERROR");
      }
      if (normalized === "info") {
        return t("INFO");
      }
      return handleStatusName(state, t);
    },
    [t]
  );

  const formatTypeLabel = useCallback(
    (type?: string | null) => {
      if (!type) {
        return t("NOT_AVAILABLE");
      }
      const normalized = type.toLowerCase();
      if (normalized === "info") {
        return t("INFO");
      }
      if (normalized === "error") {
        return t("ERROR");
      }
      if (normalized === "warning") {
        return t("WARNING");
      }
      return type;
    },
    [t]
  );

  const renderUser = useCallback(
    (user: ActivityLogItem["user"]) => {
      if (!user) {
        return <EmptyValue>{t("NOT_AVAILABLE")}</EmptyValue>;
      }

      const name = user.full_name || user.display || user.email;
      const meta: string[] = [];

      if (user.email) {
        meta.push(user.email);
      }
      if (user.id) {
        meta.push(`#${user.id}`);
      }

      return (
        <UserInfo>
          <UserName title={name || undefined}>
            {name || t("NOT_AVAILABLE")}
          </UserName>
          {meta.length > 0 ? (
            <UserMeta>
              {meta.map((value, index) => (
                <span key={`${value}-${index}`}>{value}</span>
              ))}
            </UserMeta>
          ) : null}
        </UserInfo>
      );
    },
    [t]
  );

  const toTableRow = useCallback(
    (activity: ActivityLogItem) => {
      const stateValue = activity.state || activity.type || "";
      const parametersText =
        typeof activity.parameters === "string"
          ? activity.parameters
          : activity.parameters
          ? JSON.stringify(activity.parameters, null, 2)
          : null;

      return {
        ...activity,
        state: (
          <StateWrapper>
            <GreenDot active={stateValue} />
            <Greentext style={{ textTransform: "capitalize" }} active={stateValue}>
              {formatStateLabel(stateValue)}
            </Greentext>
          </StateWrapper>
        ),
        type: formatTypeLabel(activity.type),
        log: (
          <LogContainer>
            <LogPrimary title={activity.log}>{activity.log}</LogPrimary>
            {activity.details ? (
              <LogDetails title={activity.details}>{activity.details}</LogDetails>
            ) : null}
            {parametersText ? (
              <LogDetails $mono $muted title={parametersText}>
                {parametersText}
              </LogDetails>
            ) : null}
          </LogContainer>
        ),
        user: renderUser(activity.user),
        created_at: activity.created_at
          ? timeConverterMMDDYYHHMM(activity.created_at)
          : "-",
        last_update: activity.last_update
          ? timeConverterMMDDYYHHMM(activity.last_update)
          : <EmptyValue>—</EmptyValue>,
        tasks_count:
          typeof activity.tasks_count === "number"
            ? activity.tasks_count
            : 0,
        total_operation_count:
          typeof activity.total_operation_count === "number"
            ? activity.total_operation_count
            : 0,
      };
    },
    [formatStateLabel, formatTypeLabel, renderUser]
  );

  const fetchLogs = useCallback(() => {
    const currentPage = page || 1;
    const currentPageSize = pageSize || 10;
    const currentOrdering = ordering || DEFAULT_ORDERING;

    setPageLoading(true);
    utilsService
      .getActivityLog({
        page: currentPage,
        pageSize: currentPageSize,
        ordering: currentOrdering,
      })
      .then((res) => {
        const items: ActivityLogItem[] = res.data?.objects || [];
        setLogs(items.map((activity) => toTableRow(activity)));
        setPaginationData(res.data);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setPageLoading(false);
      });
  }, [ordering, page, pageSize, toTableRow]);

  useEffect(() => {
    if (!ordering) {
      return;
    }
    fetchLogs();
  }, [fetchLogs, ordering]);

  const columns = useMemo(
    () => [
      {
        title: "ID",
        key: "id",
      },
      {
        title: t("STATE"),
        key: "state",
      },
      {
        title: t("TYPE"),
        key: "type",
      },
      {
        title: t("LOG"),
        key: "log",
      },
      {
        title: t("USER"),
        key: "user",
      },
      {
        title: t("CREATED_AT"),
        key: "created_at",
        sorted: true,
      },
      {
        title: t("LAST_UPDATE"),
        key: "last_update",
      },
      {
        title: t("TASKS"),
        key: "tasks_count",
      },
      {
        title: t("OPERATIONS"),
        key: "total_operation_count",
      },
    ],
    [t]
  );

  return (
    <Container>
      {pageLoading ? <TopProgress loading={pageLoading} /> : null}
      <WrapperRow>
        <Title>{t("ACTIVITY_LOG")}</Title>
      </WrapperRow>
      {pageLoading ? (
        <>
          <TableSkeleton
            rows={10}
            columns={columns.length}
            columnsData={columns}
          />
        </>
      ) : logs.length > 0 ? (
        <WrapperRow style={{ flexDirection: "column", marginTop: "20px" }}>
          <Table
            columns={columns}
            rows={logs}
            hideCheckbox
            paginationData={paginationData}
            updateData={fetchLogs}
          />
        </WrapperRow>
      ) : (
        <WrapperRow style={{ marginTop: "20px" }}>
          <BodyText>{t("YOU_HAVE_NO_ACTIVITY_LOGS")}</BodyText>
        </WrapperRow>
      )}
    </Container>
  );
};

export default ActivityLog;
